// supabaseClient — the app's Postgres backend seam (supabase-staging branch).
//
// USE_SUPABASE flips the whole data layer: when VITE_DATA_BACKEND=supabase,
// modules read/write the Supabase Postgres staging project instead of
// Firestore. The Firestore code paths stay byte-identical and remain the
// default — this file is inert unless the flag is set in .env.local.
//
// Identity: users sign in with Supabase Auth (bridged accounts created by
// supabase/scripts/create-auth-users.mjs; auth.users.id ↔ public.users.auth_id).
// RLS resolves the app user through that mapping, so the browser client holds
// only the public anon key — row access is enforced server-side.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { liveDatabaseEnabled, liveDatabaseUrl, sessionStorageKey } from './liveDatabase';

export const USE_SUPABASE =
  (import.meta as any).env?.VITE_DATA_BACKEND === 'supabase';

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  if (!client) {
    // The database in use (the database-only switch, services/liveDatabase.ts): the main, or the backup while it
    // has taken over the database only. With the switch off this is VITE_SUPABASE_URL, as always.
    const url = liveDatabaseEnabled() ? liveDatabaseUrl() : (import.meta as any).env?.VITE_SUPABASE_URL;
    const key = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;
    if (!url || !key) throw new Error('Supabase env missing (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)');
    const storageKey = sessionStorageKey();
    client = createClient(url, key, {
      // Explicit, not defaults-by-accident: staff tabs stay open for days. The
      // session persists across restarts and the access token is refreshed in
      // the background for as long as the refresh token is valid. One storage
      // key whichever database is used, so a switch never logs anyone out.
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, ...(storageKey ? { storageKey } : {}) },
      // Every read, write and RPC the client makes goes through this one fetch,
      // so the "expired access token" recovery lives in a single place instead
      // of at hundreds of call sites (see makeAuthAwareFetch): a 401 triggers
      // one refresh and a retry; a refresh the server REJECTS means the session
      // is truly dead, so we sign out — which routes the app to the login
      // screen instead of leaving every panel throwing 401s ("stuck with
      // errors"). A refresh that merely could not reach the server keeps the
      // session: a network blip must not log a dispatcher out.
      global: {
        fetch: makeAuthAwareFetch({
          baseFetch: async (input: any, init?: any) => {
            const res = await globalThis.fetch(input, init);
            notePolledWrite(input, init, res);
            return res;
          },
          refresh: refreshAccessToken,
          refreshOutcome: refreshSessionOutcome,
          onDeadSession,
        }),
      },
    });
    installForegroundRefresh();
  }
  return client;
}

// ── keep a foregrounded staff session alive without a hard logout ────────────
// A tab that was hidden or throttled (Safari especially) can wake holding an
// expired access token; autoRefreshToken's timer may not have fired yet, so the
// first request 401s. Refresh proactively the moment the tab is shown again, so
// that first request already carries a live token.
let foregroundRefreshInstalled = false;
function installForegroundRefresh(): void {
  if (foregroundRefreshInstalled || typeof document === 'undefined') return;
  foregroundRefreshInstalled = true;
  const wake = () => { if (document.visibilityState === 'visible') void ensureFreshSession(); };
  document.addEventListener('visibilitychange', wake);
  if (typeof window !== 'undefined') window.addEventListener('focus', () => void ensureFreshSession());
}

/** What one refresh attempt told us. `dead` is true only when the server
 *  REJECTED the refresh token (revoked, rotated away, session deleted — the
 *  "someone logged this account out elsewhere" case, 2026-09-12) or there was
 *  no session to refresh. A refresh that never reached the server (offline,
 *  5xx) is NOT dead: token null, dead false, session kept. */
export interface RefreshOutcome { token: string | null; dead: boolean }

// One refresh at a time, shared by every concurrent 401. Refresh-token rotation
// makes parallel refreshes race (the loser can invalidate the winner), so a
// burst of 401s must coalesce onto a single refresh.
let refreshInFlight: Promise<RefreshOutcome> | null = null;
export function refreshSessionOutcome(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = supabase().auth
      .refreshSession()
      .then(({ data, error }): RefreshOutcome => {
        if (error) return { token: null, dead: !isTransientAuthError(error) };
        const token = data.session?.access_token || null;
        return token ? { token, dead: false } : { token: null, dead: true };
      })
      .catch((e): RefreshOutcome => ({ token: null, dead: !isTransientAuthError(e) }))
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/** auth-js marks a refresh that could not reach the server (offline, 5xx,
 *  aborted) as retryable; every other refresh failure is the server saying
 *  no. A thrown non-auth error (a real network TypeError) is transient too. */
export function isTransientAuthError(e: unknown): boolean {
  const name = String((e as any)?.name || '');
  if (name === 'AuthRetryableFetchError') return true;
  if (name === 'AuthApiError' || name === 'AuthSessionMissingError' || name === 'AuthError') return false;
  const status = Number((e as any)?.status || 0);
  if (status >= 500) return true;
  if (status >= 400) return false;
  return !(e as any)?.__isAuthError;
}

/** The refreshed access token, or null when there is none — callers that only
 *  need a bearer. Pair with refreshSessionOutcome when "why null" matters. */
export function refreshAccessToken(): Promise<string | null> {
  return refreshSessionOutcome().then((o) => o.token);
}

// Sign out exactly once when the session is proven dead. signOut() clears the
// persisted tokens and emits SIGNED_OUT, which AppContext turns into
// setCurrentUser(null) → the login screen. Guarded so a burst of dead 401s does
// not fire a stampede of sign-outs.
//
// scope: 'local' on purpose (2026-09-12). The default scope is 'global', which
// deletes EVERY session of the account on the server — and the dispatch
// accounts are shared between several computers. A zombie tab cleaning itself
// up must not log the rest of the office out and restart the cycle.
let signingOut = false;
export function onDeadSession(): void {
  if (signingOut) return;
  signingOut = true;
  void supabase().auth.signOut({ scope: 'local' }).catch(() => {}).finally(() => { signingOut = false; });
}

/** The users read came back with NO rows. A running system always has users
 *  (at least the person signed in), and RLS hands an empty list — not an error —
 *  to a request without a live login. So an empty roster means the login is
 *  gone: refresh once; if the server refuses, sign this browser out (→ Login).
 *  A refresh that could not reach the server keeps the session; the caller
 *  keeps the roster it has and the next poll asks again. Resolves true when it
 *  signed out. (Replaces the old "System Initialization" screen, which an empty
 *  read used to open on wolfdrivers.com — Umer, 2026-10-22.) */
export async function onEmptyRoster(): Promise<boolean> {
  const outcome = await refreshSessionOutcome();
  if (outcome.dead) { onDeadSession(); return true; }
  return false;
}

type FetchLike = (input: any, init?: any) => Promise<Response>;

/** Wrap a fetch so an expired-token 401 is transparently recovered:
 *  refresh once, retry with the fresh bearer; if the server rejects the
 *  refresh, hand the session to onDeadSession (→ sign out → login) and let the
 *  original 401 surface. A refresh that could not reach the server surfaces
 *  the 401 but keeps the session. A 401 from the auth endpoints themselves IS
 *  the dead-session signal, so those are never retried (and never recurse).
 *  Exported for tests. `refresh` alone means "null = dead" (the original
 *  contract); pass `refreshOutcome` to tell dead from transient. */
export function makeAuthAwareFetch(opts: {
  baseFetch: FetchLike;
  refresh: () => Promise<string | null>;
  refreshOutcome?: () => Promise<RefreshOutcome>;
  onDeadSession: () => void;
  isAuthEndpoint?: (url: string) => boolean;
}): FetchLike {
  const isAuth = opts.isAuthEndpoint ?? ((u: string) => u.includes('/auth/v1/'));
  return async (input: any, init?: any) => {
    const res = await opts.baseFetch(input, init);
    if (res.status !== 401) return res;
    const url = typeof input === 'string' ? input : (input?.url ?? String(input));
    if (isAuth(url)) return res;
    const outcome = opts.refreshOutcome
      ? await opts.refreshOutcome()
      : await opts.refresh().then((token) => ({ token, dead: !token }));
    if (!outcome.token) {
      if (outcome.dead) opts.onDeadSession();
      return res;
    }
    const headers = new Headers((init?.headers as any) ?? (typeof input !== 'string' ? input?.headers : undefined));
    headers.set('Authorization', `Bearer ${outcome.token}`);
    return opts.baseFetch(input, { ...(init ?? {}), headers });
  };
}

// ── session freshness (2026-09-09) ──────────────────────────────────────────
// In-house staff must never be logged out. Supabase sessions do not expire on
// their own, but the access token rotates hourly and a tab that was throttled
// in the background (Safari especially) can hold an expired one for a while.
// Every request that carries the token goes through these two helpers: the
// token is refreshed when it is about to expire, and a request refused for an
// expired token is retried once after a refresh instead of surfacing
// "Session expired" to the dispatcher.
const REFRESH_AHEAD_MS = 2 * 60 * 1000;

export async function getAccessToken(): Promise<string | null> {
  const sb = supabase();
  const { data } = await sb.auth.getSession();
  const session = data.session;
  if (!session) return null;
  const expiresAt = (session.expires_at || 0) * 1000;
  if (expiresAt && expiresAt - Date.now() < REFRESH_AHEAD_MS) {
    const token = await refreshAccessToken();
    if (token) return token;
  }
  return session.access_token || null;
}

/** Refresh now if the access token is within REFRESH_AHEAD_MS of expiry.
 *  Best-effort and never throws — safe to call from a focus/visibility handler
 *  or before a burst of writes. */
export async function ensureFreshSession(): Promise<void> {
  try {
    const { data } = await supabase().auth.getSession();
    const session = data.session;
    if (!session) return;
    const expiresAt = (session.expires_at || 0) * 1000;
    if (expiresAt && expiresAt - Date.now() < REFRESH_AHEAD_MS) await refreshAccessToken();
  } catch {
    /* transient — the 401 interceptor still covers the next request */
  }
}

/** True for the errors PostgREST / GoTrue / our functions give back when the
 *  bearer token is expired or invalid — the cases a refresh can cure. */
export function isSessionError(e: unknown): boolean {
  const msg = String((e as any)?.message || (e as any)?.error || e || '');
  const code = String((e as any)?.code || (e as any)?.status || '');
  return /jwt expired|jwt|session expired|invalid claim|401/i.test(msg) || code === 'PGRST301' || code === '401';
}

/** Run a Supabase-authenticated operation; if it fails because the token was
 *  expired, refresh the session once and run it again. */
export async function withFreshSession<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (!isSessionError(e)) throw e;
    const { token, dead } = await refreshSessionOutcome();
    if (!token) {
      // The server rejected the refresh token — the session is genuinely over.
      // Route to the login screen instead of surfacing "Session expired" and
      // leaving the user on a broken authed page. (A refresh that never
      // reached the server keeps the session; the error just surfaces.)
      if (dead) onDeadSession();
      throw e;
    }
    return await fn();
  }
}

// ── cache-first render (stale-while-revalidate) ─────────────────────────────
// Firestore's onSnapshot paints instantly from its IndexedDB cache, then
// updates from the network. Supabase has no local cache, so every page used to
// block first paint on a network round-trip ("data late loading"). We persist
// each subscription's last rows to localStorage and paint them immediately on
// mount, then revalidate from the network. The cache is cleared whenever the
// signed-in user changes (syncSupabaseCacheUser), so one viewer never sees
// another's rows; a page refresh with the SAME user keeps the warm cache.
const CACHE_PREFIX = 'sbcache:v1:';
export const readListCache = (key: string): any[] | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
};
export const writeListCache = (key: string, rows: any[]): void => {
  let json: string;
  try { json = JSON.stringify(rows); } catch { return; }
  try { localStorage.setItem(CACHE_PREFIX + key, json); return; }
  catch { /* quota exceeded / private mode — try to make room below */ }
  // Quota is per origin and shared by every list this app caches. A cache key
  // encodes the query (table, order, limit, column list), so when a caller's
  // column list changes, its OLD entry — often megabytes for `emails` — stays
  // behind under the old key and can crowd the new one out for good. The
  // symptom is a page that never paints from cache again and waits on the
  // network on every load. Evict every other entry for the same table and
  // retry once; if it still won't fit, skip caching this list.
  try {
    const table = key.split(':')[0];
    const mine = CACHE_PREFIX + key;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k !== mine && k.startsWith(`${CACHE_PREFIX}${table}:`)) localStorage.removeItem(k);
    }
    localStorage.setItem(mine, json);
  } catch { /* still no room — this list just won't be cache-painted */ }
};
export function clearSupabaseCache(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}
// Clear the render cache only when the signed-in user changes (a different
// account), keeping the cache warm across refreshes for the same user. Call
// from the auth handler with the current uid, or null on sign-out.
export function syncSupabaseCacheUser(uid: string | null): void {
  try {
    const KEY = 'sbcache:uid';
    const last = localStorage.getItem(KEY);
    if (last !== (uid || '')) {
      clearSupabaseCache();
      if (uid) localStorage.setItem(KEY, uid); else localStorage.removeItem(KEY);
    }
  } catch { /* ignore */ }
}

// ── list subscription helper ────────────────────────────────────────────────
// Firestore's onSnapshot hands the app a fresh full array on every change.
// This reproduces that contract: initial fetch + a realtime channel that
// patches the in-memory rows from each change payload (see "realtime
// patching helpers" below) and re-fetches only as a fallback. Callers get the
// same "here's the whole list" callback shape their Firestore code used.
// PostgREST answers at most 1000 rows per request (Supabase's max-rows) and
// says nothing when it truncates. Any read that can exceed that must page:
// build the query fresh for every page (the builder is single-use), ask for
// [offset, offset+PAGE-1], stop when a page comes back short. Ordering keeps
// the pages stable — callers pass their own order; without one we still page
// (PostgREST keeps a stable physical order between two quick requests).
export const PG_PAGE = 1000;
// Keyset pages are smaller on purpose: each one is a fresh indexed lookup, so
// more of them costs little, and a smaller page keeps every request well
// inside the statement timeout even when the row carries embedded children.
export const PG_KEYSET_PAGE = 500;

/**
 * Page through a table.
 *
 * `keyset` picks the strategy, and on a big table the choice decides whether
 * the read works at all:
 *
 *   without it — OFFSET paging. Page N asks the database to produce and throw
 *     away N x pageSize rows first. On production `orders` (11k rows, ~1.9k
 *     matching, two embedded child tables) page 2 took >8s and came back
 *     `57014 canceling statement due to statement timeout`. fetchAllRows then
 *     threw, the caller's onRows never fired, and BOTH the web and the phones
 *     rendered "0 of 0 orders" while the data sat there intact (2026-09-09).
 *
 *   with it — KEYSET paging. Every page is `<column> <= cursor ... LIMIT n`,
 *     an indexed seek that costs the same whether it is page 1 or page 40.
 *     The same read finished all 1,931 rows in four pages, none over 4s.
 *
 * The boundary is inclusive (`lte`/`gte`) so rows sharing the cursor's exact
 * value cannot fall through the crack between two pages; the repeats are
 * dropped by id. If a page yields nothing new — only possible when more than
 * `pageSize` rows share one cursor value — it stops rather than loop forever.
 */
export async function fetchAllRows<Row = any>(
  build: () => any,
  pageSize: number = PG_PAGE,
  keyset?: { column: string; ascending?: boolean },
): Promise<Row[]> {
  const out: Row[] = [];
  if (!keyset) {
    for (let offset = 0; ; offset += pageSize) {
      const { data, error } = await build().range(offset, offset + pageSize - 1);
      if (error) throw new Error(error.message || String(error)); // a real Error, like every other reader
      const rows: Row[] = data || [];
      out.push(...rows);
      if (rows.length < pageSize) return out;
    }
  }
  const ascending = keyset.ascending ?? true;
  const seen = new Set<string>();
  let cursor: any = null;
  for (;;) {
    let q = build();
    if (cursor != null) q = ascending ? q.gte(keyset.column, cursor) : q.lte(keyset.column, cursor);
    const { data, error } = await q.range(0, pageSize - 1);
    if (error) throw new Error(error.message || String(error));
    const rows: Row[] = data || [];
    let added = 0;
    for (const r of rows) {
      const id = (r as any)?.id;
      const key = id == null ? null : String(id);
      if (key !== null) {
        if (seen.has(key)) continue;   // the inclusive boundary row, already taken
        seen.add(key);
      }
      out.push(r);
      added++;
    }
    if (rows.length < pageSize) return out;
    const next = (rows[rows.length - 1] as any)?.[keyset.column];
    // No cursor to advance to, or a page that moved nothing: stop. Looping here
    // would hammer the database for rows it is never going to hand over.
    if (next == null || next === cursor || added === 0) return out;
    cursor = next;
  }
}

// ── realtime patching helpers ───────────────────────────────────────────────
// A postgres_changes payload already carries the changed row (`new` on
// INSERT/UPDATE, the primary key in `old` on DELETE). Every list subscription
// below keeps its rows in a Map keyed by id and patches that Map from the
// payload instead of re-downloading the whole table — the callers still get
// the same "here is the whole list" callback. A full re-fetch is the fallback,
// never the default: it runs when a payload lacks an id, when the channel
// drops (CHANNEL_ERROR / TIMED_OUT) and when it re-joins after a drop (resync).
export interface RealtimePayload {
  eventType?: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  new?: any;
  old?: any;
  table?: string;
}
export type PatchOutcome = 'patched' | 'noop' | 'refetch';
/** Handed to a `subscribeTable(..., { external })` caller — see that option. */
export interface ExternalTableControl {
  /** Apply one change exactly as a postgres_changes payload would be applied
   *  (INSERT/UPDATE carry the full row in `new`, DELETE the key in `old`). */
  change: (payload: RealtimePayload) => void;
  /** Re-download the list (debounced 250 ms). */
  refetch: () => void;
}
export type SubscribeMode = 'patch' | 'refetch';

/**
 * mustAffectRows — run a write that is only correct if it changed something.
 *
 * PostgREST answers an UPDATE matching zero rows with 200 and an empty body, and
 * supabase-js reports no error, so a write blocked by RLS is indistinguishable
 * from one that worked. The app carries on, shows a toast, and the data never
 * existed.
 *
 * On 2026-09-09 a re-up of 687 units across 42 line items went through
 * updateStoreStockQuantity below. store_stocks_write was is_dispatch_tier() and
 * both stores accepted their own delivery as role `store`, so every upsert was
 * denied. `movements` and `store_history` recorded normally, so the delivery
 * looked complete everywhere except the number that matters — and it surfaced a
 * day later through the client rather than the app.
 *
 * Appending .select() makes PostgREST return the affected rows, so an empty
 * result is detectable. Mirrors Dispatch-Mobile-App/services/supabaseClient.ts.
 *
 * Use for any write whose whole purpose is to change a row. NOT for best-effort
 * writes (audit trails, read receipts) where zero rows is legitimate.
 */
export async function mustAffectRows(
  label: string,
  build: () => any,
): Promise<any[]> {
  const { data, error } = await build().select();
  if (error) throw new Error(`${label}: ${error.message}`);
  if (!data || data.length === 0) {
    throw new Error(
      `${label}: wrote 0 rows — the row does not exist, or RLS denied the write`,
    );
  }
  return data;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** SQL LIKE pattern → RegExp (case-sensitive, `%`/`_` wildcards, backslash escapes). */
export const likeToRegExp = (pattern: string): RegExp => {
  let out = '^';
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i];
    if (ch === '\\' && i + 1 < pattern.length) { out += escapeRe(pattern[++i]); continue; }
    if (ch === '%') out += '[\\s\\S]*';
    else if (ch === '_') out += '[\\s\\S]';
    else out += escapeRe(ch);
  }
  return new RegExp(out + '$');
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;
/** Comparator matching PostgREST's `.order(column, { ascending })`: Postgres
 *  puts NULLs last when ascending and first when descending; timestamptz
 *  strings compare by instant (PostgREST says `+00:00`, the browser wrote `Z`). */
export const compareRows = (column: string, ascending: boolean) => (a: any, b: any): number => {
  let av = a?.[column];
  let bv = b?.[column];
  if (av == null && bv == null) return 0;
  if (av == null) return ascending ? 1 : -1;
  if (bv == null) return ascending ? -1 : 1;
  if (typeof av === 'string' && typeof bv === 'string' && ISO_DATE.test(av) && ISO_DATE.test(bv)) {
    const ad = Date.parse(av); const bd = Date.parse(bv);
    if (!Number.isNaN(ad) && !Number.isNaN(bd)) { av = ad; bv = bd; }
  }
  if (av < bv) return ascending ? -1 : 1;
  if (av > bv) return ascending ? 1 : -1;
  return 0;
};

/** Patch `rows` (id → raw row) from one realtime payload. INSERT/UPDATE set the
 *  row (merged over the existing one, so embeds a payload never carries
 *  survive); DELETE removes by `old.id`. `accept` mirrors the subscription's
 *  server-side filter: a row that fails it is dropped rather than added. */
export function applyRealtimeChange(
  rows: Map<string, any>,
  payload: RealtimePayload | undefined,
  accept?: (row: any) => boolean,
  // Primary-key column. Defaults to `id`, but not every table has one:
  // sms_threads is keyed by thread_key, forwarded_sms by id. Without this the
  // patch path bails to a full re-download for any such table.
  pk: string = 'id',
): PatchOutcome {
  if (!payload || typeof payload !== 'object') return 'refetch';
  const type = payload.eventType;
  if (type === 'INSERT' || type === 'UPDATE') {
    const row = payload.new;
    const id = row?.[pk];
    if (id == null) return 'refetch';
    const key = String(id);
    if (accept && !accept(row)) return rows.delete(key) ? 'patched' : 'noop';
    const existing = rows.get(key);
    rows.set(key, existing ? { ...existing, ...row } : row);
    return 'patched';
  }
  if (type === 'DELETE') {
    const id = payload.old?.[pk];
    if (id == null) return 'refetch';
    return rows.delete(String(id)) ? 'patched' : 'noop';
  }
  return 'refetch';
}

/** Wrap a row mapper so an UNCHANGED raw row maps to the SAME object as last
 *  time (a WeakMap keyed by the raw row). Realtime patching replaces only the
 *  changed row's raw object, so every other mapped row keeps its identity —
 *  which is what lets a React.memo card or bubble skip re-rendering when one
 *  sibling changes. A full re-fetch hands back all-new raw rows, so everything
 *  maps afresh there (correct: the whole list may have changed). */
export const stableMapper = <T,>(map: (row: any) => T): ((row: any) => T) => {
  const cache = new WeakMap<object, T>();
  return (row: any) => {
    if (row === null || typeof row !== 'object') return map(row);
    const hit = cache.get(row);
    if (hit !== undefined) return hit;
    const mapped = map(row);
    cache.set(row, mapped);
    return mapped;
  };
};

// ── Recovering from a Realtime drop without stampeding the database ─────────
// When Realtime loses its database pool it drops EVERY channel in EVERY tab at
// the same instant. The old policy refetched the whole table on the drop and
// again on the re-join, 250 ms later, for ~30 subscriptions per tab: the whole
// office hit a database that was already struggling, twice, in step (incident
// 2026-09-18..20). Now:
//   • the DROP refetches nothing — the database is the thing that is sick, and
//     the re-join refetch covers whatever was missed;
//   • the RE-JOIN refetches once, after a random delay, so subscriptions and
//     tabs spread out instead of firing together;
//   • if a channel stays down, ONE late refetch keeps the data from going stale.
export const RESYNC_JITTER_MS: [number, number] = [500, 8000];
// The tab someone is LOOKING at re-reads soon (2026-09-30, Umer: 3–7 s after coming back online felt slow).
// About one visible tab per person, so this adds no burst; background tabs keep the long spread above.
export const RESYNC_VISIBLE_JITTER_MS: [number, number] = [300, 1500];
const rejoinRange = (): [number, number] =>
  typeof document !== 'undefined' && document.visibilityState === 'visible' ? RESYNC_VISIBLE_JITTER_MS : RESYNC_JITTER_MS;
export const RESYNC_STILL_DOWN_MS: [number, number] = [45000, 75000];
let resyncDelay = (range: [number, number]): number => range[0] + Math.random() * (range[1] - range[0]);
/** Test seam: make the delays deterministic. Pass nothing to restore the default. */
export function _setResyncDelay(fn?: (range: [number, number]) => number): void {
  resyncDelay = fn ?? ((range) => range[0] + Math.random() * (range[1] - range[0]));
}

/** One pending resync shared by several channels of the same list (Phase 1, 2026-09-30).
 *  The order list rides three channels (orders, order_items, order_photos); with a timer
 *  each, one drop re-read "active + 30 days of orders with items and photos" up to three
 *  times per tab, 0.5–8 s apart. A request made while a resync is already due no later
 *  than it would be is covered by that one (it reads after this request); a sooner
 *  request brings it forward; a request after it fired schedules a new one. */
export const sharedResync = (resync: () => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let due = 0;
  return {
    request(range: [number, number]) {
      const at = Date.now() + resyncDelay(range);
      if (timer && due <= at) return;
      if (timer) clearTimeout(timer);
      due = at;
      timer = setTimeout(() => { timer = null; resync(); }, at - Date.now());
    },
    cancel() {
      if (timer) { clearTimeout(timer); timer = null; }
    },
  };
};

/** Shared channel-status policy: never on the first join (the initial fetch
 *  already ran); one jittered resync when the channel re-joins after a drop;
 *  one late resync if it is still down; never repeatedly while it stays down.
 *  `shared` (from sharedResync) lets several channels of one list share the timer. */
export const channelResync = (resync: () => void, shared?: ReturnType<typeof sharedResync>) => {
  let everSubscribed = false;
  let live = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const later = (range: [number, number]) => {
    if (shared) { shared.request(range); return; }
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; resync(); }, resyncDelay(range));
  };
  return (status: string) => {
    if (status === 'SUBSCRIBED') {
      // ANY re-join resyncs, whatever the drop was reported as: a closed socket
      // can surface as CLOSED rather than CHANNEL_ERROR, and skipping the resync
      // then would leave the list stale with nothing to correct it.
      if (everSubscribed) later(rejoinRange());
      everSubscribed = true;
      live = true;
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      if (live) { live = false; later(RESYNC_STILL_DOWN_MS); }
    } else if (status === 'CLOSED') {
      // Either the subscriber unsubscribed (the callback's own `dead` guard makes
      // a late resync a no-op) or the socket closed; a re-join will resync.
      // A shared resync belongs to the other channels too: one closing keeps it.
      live = false;
      if (timer) { clearTimeout(timer); timer = null; }
    }
  };
};


// ── polled tables (no Realtime channel) ─────────────────────────────────────
// A `postgres_changes` binding is not free: for EVERY change to the table,
// Realtime runs the table's security policy once per subscriber. `users` was
// bound whole-table by every open tab while each on-duty driver's phone rewrote
// its row with GPS about once a minute — tabs × drivers × policy, all day, for a
// field the web console throws away. That load is what made Realtime's change
// handling slow enough to lose its database pool (incident 2026-09-18..20).
//
// `live: false` keeps the same subscribeTable contract with no channel at all:
// cache-first paint, one fetch, then a re-fetch every `pollMs` while the tab is
// visible, at once when it becomes visible again, and straight after THIS tab
// writes to the table (so an admin's own edit shows immediately; another
// person's edit shows within one poll).
const polledRefreshers = new Map<string, Set<() => void>>();
/** Re-fetch every polled subscription of `table` in this tab, now. */
export function refreshPolledTable(table: string): void {
  polledRefreshers.get(table)?.forEach((fn) => fn());
}
/** Called for every request the client makes: a successful write to a polled
 *  table refreshes it. One place, so no call site has to remember to. */
function notePolledWrite(input: any, init: any, res: { ok?: boolean } | undefined): void {
  if (!polledRefreshers.size || !res?.ok) return;
  const method = String(init?.method || (typeof input !== 'string' ? input?.method : '') || 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return;
  const url = typeof input === 'string' ? input : (input?.url ?? String(input));
  const m = /\/rest\/v1\/([A-Za-z0-9_]+)/.exec(url);
  if (m && polledRefreshers.has(m[1])) refreshPolledTable(m[1]);
}
/** Test seam. */
export function _resetPolledTables(): void { polledRefreshers.clear(); }

export function subscribeTable<T>(
  table: string,
  map: (row: any) => T,
  onRows: (rows: T[]) => void,
  opts?: {
    order?: { column: string; ascending?: boolean };
    limit?: number;
    // Server-side LIKE on one column. Use it whenever the caller only wants a
    // slice of a big table: PostgREST caps an unfiltered read at 1000 rows
    // (Supabase's max-rows), so "list everything and filter client-side"
    // silently drops rows once the table outgrows that — the Messages page
    // lost board threads this way when conversations passed 1000 rows.
    like?: { column: string; pattern: string };
    onError?: (e: Error) => void;
    /** `false` = no Realtime channel; re-fetch on a timer instead (see "polled
     *  tables" above). Default true. */
    live?: boolean;
    /** Poll interval for `live: false`. Default 30 s; ±20 % jitter per cycle. */
    pollMs?: number;
    // 'patch' (default): apply each realtime payload to the in-memory rows.
    // 'refetch': the old behaviour — re-download the table (debounced) on any
    // change. Opt out per caller when a table has no `id` primary key.
    mode?: SubscribeMode;
    // Column list for the select (default '*'). A narrow select changes the
    // cache key so full-row consumers never paint from a thin cache.
    select?: string;
    // Primary-key column (default 'id'). sms_threads is keyed by thread_key —
    // without naming it, every realtime payload falls back to a full re-fetch.
    pk?: string;
    // Server-side equality filters, all ANDed. They narrow the QUERY and the
    // accept predicate — a patched row that no longer matches is dropped.
    eq?: { column: string; value: string | number | boolean }[];
    // Server-side inequality filters, all ANDed — the same query + accept rule as `eq`: a row that
    // TAKES the value is dropped from the list (the Order Board asks for stage <> 'cancelled', so a
    // card that is cancelled leaves every open board on its own, 2026-10-06).
    neq?: { column: string; value: string | number | boolean }[];
    // A server-side filter in PostgREST `or` syntax with its JavaScript mirror: query + accept, never
    // the channel (the Order Board's live rows: active cards plus the last two Ottawa days of cancelled
    // and archived ones, 2026-10-06). `key` names it in the cache key; `columns` are the ones the mirror
    // reads, kept on a trimmed payload.
    where?: { or: string; accept: (row: any) => boolean; key: string; columns?: string[] };
    // Array-column containment (PostgREST `cs`): only rows whose `column`
    // holds `value`. Query + accept filter, never the channel — the payload
    // that says a row LOST the value is exactly the one that must arrive so the
    // row can be dropped. Exists for subscribeBlockedNumbers: seen on
    // wolfdrivers.com walking ~5,000 contacts, five pages of select=*, on every
    // page of the app, to find the handful tagged Blocked.
    contains?: { column: string; value: string };
    // Narrows the REALTIME CHANNEL too (PostgREST filter syntax, one condition).
    // Deliberately separate from `eq`: a channel filter also hides the payload
    // that says a row STOPPED matching. Use it only where that is safe and the
    // volume justifies it — `thread_key=eq.X` on an open conversation, where a
    // row can never change threads. Never for a flag like `otp`, which is
    // sticky-settable: filtering the channel on it would leave a thread that
    // just became OTP sitting stale in the list forever.
    channelFilter?: string;
    /** Live data v2 (services/live): NO postgres_changes binding. The same
     *  cache-first paint and first fetch, then the CALLER drives the list from
     *  its own source (a private Broadcast topic carrying id-only hints): it is
     *  handed a controller and returns its own cleanup. Absent = today's path. */
    external?: (ctl: ExternalTableControl) => () => void;
  },
): () => void {
  let dead = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const mode: SubscribeMode = opts?.mode ?? 'patch';
  const select = opts?.select || '*';
  const pk = opts?.pk || 'id';
  const cacheKey =
    `${table}:${opts?.order?.column || ''}:${opts?.order?.ascending ?? ''}:${opts?.limit || ''}:${opts?.like ? `${opts.like.column}~${opts.like.pattern}` : ''}` +
    (opts?.eq ? `:${opts.eq.map((f) => `${f.column}=${f.value}`).join(',')}` : '') +
    (opts?.neq ? `:${opts.neq.map((f) => `${f.column}!=${f.value}`).join(',')}` : '') +
    (opts?.where ? `:where=${opts.where.key}` : '') +
    (opts?.contains ? `:${opts.contains.column}@>${opts.contains.value}` : '') +
    (select !== '*' ? `:${select}` : '');

  // Current rows by id (raw Postgres rows; mapped on every emit — unchanged
  // rows keep their mapped identity, see stableMapper).
  const rows = new Map<string, any>();
  const mapRow = stableMapper(map);
  let ready = false;      // first full fetch landed — patches may apply
  let patchable = true;   // every fetched row had an id — patches are safe
  const likeRe = opts?.like ? likeToRegExp(opts.like.pattern) : null;
  const tests: ((r: any) => boolean)[] = [];
  if (likeRe) tests.push((r: any) => { const v = r?.[opts!.like!.column]; return typeof v === 'string' && likeRe.test(v); });
  for (const f of opts?.eq || []) tests.push((r: any) => r?.[f.column] === f.value);
  for (const f of opts?.neq || []) tests.push((r: any) => r?.[f.column] !== f.value);
  if (opts?.where) tests.push(opts.where.accept);
  if (opts?.contains) {
    const { column, value } = opts.contains;
    tests.push((r: any) => Array.isArray(r?.[column]) && r[column].includes(value));
  }
  const accept = tests.length ? (r: any) => tests.every((t) => t(r)) : undefined;

  // Cache-first: paint the last-known rows instantly, then revalidate below. Through the same accept
  // rule as a realtime row (2026-10-06): a cached row the filters would drop today never paints.
  const cached = readListCache(cacheKey);
  if (cached) { try { onRows((accept ? cached.filter(accept) : cached).map(map)); } catch { /* ignore stale/malformed cache */ } }

  // A realtime payload carries the WHOLE row, whatever `select` asked for.
  // With a plain column list, keep only those columns so a patched row has
  // the same shape as a fetched one — `emails` otherwise pulled body_html
  // (up to 180 KB a message) into memory and the localStorage cache on every
  // change (audit, 2026-09-25). Only when the key and every filter column
  // are on the list; anything fancier (embeds, aliases) uses the payload as is.
  const keep = plainColumnList(select);
  const needed = [pk, ...(opts?.eq || []).map((f) => f.column), ...(opts?.neq || []).map((f) => f.column), ...(opts?.where?.columns || [])];
  if (opts?.like) needed.push(opts.like.column);
  if (opts?.contains) needed.push(opts.contains.column);
  const trim =
    keep && needed.every((c) => keep.has(c))
      ? (row: any) => {
          const out: any = {};
          for (const c of keep) if (c in row) out[c] = row[c];
          return out;
        }
      : null;

  // The cache write is a synchronous JSON.stringify of the whole list. A sync
  // batch lands as one realtime event per row, so write it at most once a
  // second (trailing) — and always on unsubscribe, so the next cache-first
  // paint still shows the patched list.
  const CACHE_WRITE_GAP_MS = 1000;
  let cacheWrittenAt = 0;
  let cachePending: any[] | null = null;
  let cacheTimer: ReturnType<typeof setTimeout> | null = null;
  const writeCacheSoon = (list: any[]) => {
    const wait = CACHE_WRITE_GAP_MS - (Date.now() - cacheWrittenAt);
    if (wait <= 0) {
      cacheWrittenAt = Date.now();
      cachePending = null;
      writeListCache(cacheKey, list);
      return;
    }
    cachePending = list;
    if (!cacheTimer) {
      cacheTimer = setTimeout(() => {
        cacheTimer = null;
        if (!cachePending) return;
        const l = cachePending;
        cachePending = null;
        cacheWrittenAt = Date.now();
        writeListCache(cacheKey, l);
      }, wait);
    }
  };
  const flushCache = () => {
    if (cacheTimer) { clearTimeout(cacheTimer); cacheTimer = null; }
    if (cachePending) { const l = cachePending; cachePending = null; writeListCache(cacheKey, l); }
  };

  const emit = (list: any[]) => {
    if (dead) return;
    onRows(list.map(mapRow));
    writeCacheSoon(list);
  };
  const snapshot = (): any[] => {
    let list = [...rows.values()];
    if (opts?.order) list.sort(compareRows(opts.order.column, opts.order.ascending ?? true));
    if (opts?.limit && list.length > opts.limit) list = list.slice(0, opts.limit);
    return list;
  };

  const fetchNow = async () => {
    try {
      const build = () => {
        let q = supabase().from(table).select(select);
        if (opts?.like) q = q.like(opts.like.column, opts.like.pattern);
        for (const f of opts?.eq || []) q = q.eq(f.column, f.value as any);
        for (const f of opts?.neq || []) q = q.neq(f.column, f.value as any);
        if (opts?.where) q = q.or(opts.where.or);
        if (opts?.contains) q = q.contains(opts.contains.column, [opts.contains.value]);
        if (opts?.order) q = q.order(opts.order.column, { ascending: opts.order.ascending ?? true });
        return q;
      };
      let list: any[];
      if (opts?.limit) {
        const { data, error } = await build().limit(opts.limit);
        if (error) throw new Error(`${table}: ${error.message}`);
        list = data || [];
      } else {
        // No caller limit means "everything": page past the 1000-row cap.
        try { list = await fetchAllRows(build); }
        catch (e: any) { throw new Error(`${table}: ${e?.message || e}`); }
      }
      if (dead) return;
      rows.clear();
      patchable = true;
      list.forEach((r, i) => {
        if (r?.[pk] == null) patchable = false;
        rows.set(r?.[pk] == null ? `#${i}` : String(r[pk]), r);
      });
      ready = true;
      emit(list); // server order, exactly as fetched
    } catch (e: any) {
      opts?.onError?.(e);
    }
  };

  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fetchNow, 250); // coalesce bursts, keep UI live
  };
  const onChange = (payload?: RealtimePayload) => {
    if (dead) return;
    if (mode === 'refetch' || !ready || !patchable) { schedule(); return; }
    const p = trim && payload?.new && typeof payload.new === 'object' ? { ...payload, new: trim(payload.new) } : payload;
    const outcome = applyRealtimeChange(rows, p, accept, pk);
    if (outcome === 'refetch') { schedule(); return; }
    if (outcome === 'noop') return;
    const list = snapshot();
    emit(list);
    // Under a limit the map only needs the window. Rows patched in from
    // outside it (an old email marked read, say) used to pile up until the
    // next full fetch; once the map is twice the window, drop what the
    // window doesn't show. A later change to such a row simply re-adds it.
    if (opts?.limit && rows.size > opts.limit * 2) {
      const shown = new Set(list.map((r) => String(r?.[pk])));
      for (const k of [...rows.keys()]) if (!shown.has(k)) rows.delete(k);
    }
    // Under a limit, a removal may open a slot only the server can fill.
    if (opts?.limit && payload?.eventType === 'DELETE') schedule();
  };

  if (opts?.external) {
    fetchNow();
    const stop = opts.external({
      change: (payload) => onChange(payload),
      refetch: () => { if (!dead) schedule(); },
    });
    return () => {
      dead = true;
      if (timer) clearTimeout(timer);
      flushCache();
      try { stop(); } catch { /* ignore */ }
    };
  }

  if (opts?.live === false) {
    const every = opts.pollMs ?? 30000;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const arm = () => {
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = setTimeout(tick, every * (0.8 + Math.random() * 0.4));
    };
    const tick = () => { if (dead) return; if (!hidden()) void fetchNow(); arm(); };
    const refreshNow = () => { if (!dead) schedule(); };           // debounced 250 ms
    const onVisible = () => { if (!hidden()) refreshNow(); };
    let set = polledRefreshers.get(table);
    if (!set) { set = new Set(); polledRefreshers.set(table, set); }
    set.add(refreshNow);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    fetchNow();
    arm();
    return () => {
      dead = true;
      if (timer) clearTimeout(timer);
      if (pollTimer) clearTimeout(pollTimer);
      flushCache();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
      const cur = polledRefreshers.get(table);
      cur?.delete(refreshNow);
      if (cur && cur.size === 0) polledRefreshers.delete(table);
    };
  }

  fetchNow();
  const channel = supabase()
    .channel(`sub_${table}_${Math.random().toString(36).slice(2, 8)}`)
    .on(
      'postgres_changes',
      {
        event: '*', schema: 'public', table,
        ...(opts?.channelFilter ? { filter: opts.channelFilter } : {}),
      },
      onChange,
    )
    .subscribe(channelResync(() => { if (!dead) schedule(); }));

  return () => {
    dead = true;
    if (timer) clearTimeout(timer);
    flushCache();
    supabase().removeChannel(channel);
  };
}

/** `select` as a set of plain column names, or null when it is `*`, has an
 *  embed / alias / cast, or anything else a realtime row can't be cut to. */
export function plainColumnList(select: string): Set<string> | null {
  if (!select || select === '*') return null;
  if (/[()*:!>]/.test(select)) return null;
  const cols = select.split(',').map((s) => s.trim()).filter(Boolean);
  if (!cols.length || cols.some((c) => !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(c))) return null;
  return new Set(cols);
}

// ── single-row subscription ─────────────────────────────────────────────────
// One row selected by an equality filter (e.g. settings key='dayZero'). The
// realtime channel carries the SAME filter, so writes to other rows of the
// table never reach this subscriber — the whole point: the settings table also
// holds the SMS gateway heartbeat, stamped every few seconds by the Text-Inbox
// poller, and an unfiltered channel re-fetched dayZero on every stamp. The
// payload's `new` row is used directly; a re-fetch only runs when a payload
// arrives without it, or when the channel re-joins after a drop.
export function subscribeRow<T>(
  table: string,
  filter: { column: string; value: string },
  map: (row: any | null) => T,
  onValue: (value: T) => void,
  // `client`: the caller's Supabase client (defaults to this module's). Callers
  // pass their own imported `supabase()` so a test that fakes the module seam
  // reaches the helper too.
  opts?: { select?: string; onError?: (e: Error) => void; client?: SupabaseClient },
): () => void {
  let dead = false;
  const sb = () => opts?.client ?? supabase();
  const fetchNow = async () => {
    try {
      const { data, error } = await sb()
        .from(table).select(opts?.select || '*').eq(filter.column, filter.value).maybeSingle();
      if (error) throw new Error(`${table}: ${error.message}`);
      if (!dead) onValue(map(data ?? null));
    } catch (e: any) {
      opts?.onError?.(e);
    }
  };
  const onChange = (payload?: RealtimePayload) => {
    if (dead) return;
    if (payload?.eventType === 'DELETE') { onValue(map(null)); return; }
    const row = payload?.new;
    if (row && row[filter.column] === filter.value) { onValue(map(row)); return; }
    fetchNow();
  };
  fetchNow();
  const channel = sb()
    .channel(`row_${table}_${Math.random().toString(36).slice(2, 8)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table, filter: `${filter.column}=eq.${filter.value}` }, onChange)
    .subscribe(channelResync(() => { if (!dead) fetchNow(); }));
  return () => {
    dead = true;
    sb().removeChannel(channel);
  };
}

// ── joined (parent + child tables) subscription ─────────────────────────────
// For reads with PostgREST embeds (orders + order_items + order_photos): the
// parent's realtime payload has the parent columns but never the embeds, and a
// child payload only names its parent through the FK. So: a parent UPDATE
// merges the payload over the row we hold (embeds kept); a parent INSERT (or a
// parent we do not hold) — like any child change — queues the parent id and ONE
// `fetchByIds` (coalesced 150 ms) reads it WITH its embeds before it shows; a parent DELETE
// removes by id. `accept` mirrors the server-side window filter so a patched
// row that no longer qualifies (e.g. delivered outside the 30-day window)
// drops out instead of being forced to stay; a full resync (channel drop /
// re-join, id-less payload) replaces everything with the server's answer.
export function subscribeJoined<Row extends { id: string | number }, T>(cfg: {
  parent: string;
  children?: Array<{ table: string; fk: string }>;
  fetchAll: () => Promise<Row[]>;
  fetchByIds: (ids: string[]) => Promise<Row[]>;
  map: (row: Row) => T;
  onRows: (rows: T[]) => void;
  onError?: (e: Error) => void;
  // Cache-first paint key (raw rows, scoped to the signed-in user).
  cacheKey?: string;
  order?: { column: string; ascending?: boolean };
  accept?: (row: Row) => boolean;
  mode?: SubscribeMode;
  // The caller's Supabase client for the realtime channels (defaults to this
  // module's) — pass the imported `supabase()` so a faked module seam in tests
  // covers the channels the way it covers fetchAll / fetchByIds.
  client?: SupabaseClient;
  // Optional liveness taps for a caller running a polling fallback
  // (services/chat/liveness.ts). All three are inert when absent, so every
  // existing call site behaves exactly as before.
  live?: {
    /** Every channel status, from every channel this subscription owns. */
    onStatus?: (status: string) => void;
    /** Any realtime payload arrived — the socket is demonstrably alive. */
    onPayload?: () => void;
    /** Handed the full-resync trigger, so the caller's poll can fall back to it. */
    onResync?: (resync: () => void) => void;
  };
}): () => void {
  let dead = false;
  let fullTimer: ReturnType<typeof setTimeout> | null = null;
  let idsTimer: ReturnType<typeof setTimeout> | null = null;
  const sb = () => cfg.client ?? supabase();
  const mode: SubscribeMode = cfg.mode ?? 'patch';
  const cacheKey = cfg.cacheKey ? `j:${cfg.cacheKey}` : null;
  if (cacheKey) {
    const cached = readListCache(cacheKey);
    if (cached) { try { cfg.onRows(cached.map(cfg.map)); } catch { /* stale/malformed cache — ignore */ } }
  }
  const rows = new Map<string, Row>();
  const pendingIds = new Set<string>();
  const mapRow = stableMapper(cfg.map); // unchanged rows keep their mapped identity
  let ready = false;
  // A full read is a snapshot from when it STARTED (2026-10-01: a tag saved while the orders list was
  // re-reading flashed back to the old value when the slower read landed). Live changes that arrive
  // while a full read is in flight are kept here (null = gone) and laid over its answer; an older read
  // that finishes after a newer one has landed is dropped.
  let liveSeq = 0;
  let fullReads = 0;
  let fullStarted = 0;
  let fullApplied = 0;
  const sinceFull = new Map<string, { seq: number; row: Row | null }>();
  const noteLive = (key: string, row: Row | null) => {
    if (fullReads > 0) sinceFull.set(key, { seq: ++liveSeq, row });
  };

  const emit = (list: Row[]) => {
    if (dead) return;
    cfg.onRows(list.map(mapRow));
    if (cacheKey) writeListCache(cacheKey, list);
  };
  const snapshot = (): Row[] => {
    const list = [...rows.values()];
    if (cfg.order) list.sort(compareRows(cfg.order.column, cfg.order.ascending ?? true));
    return list;
  };
  const fetchNow = async () => {
    const gen = ++fullStarted;
    const fromSeq = liveSeq;
    const queuedBefore = new Set(pendingIds);
    fullReads++;
    try {
      const list = await cfg.fetchAll();
      if (dead || gen < fullApplied) return; // a newer full read already landed
      fullApplied = gen;
      rows.clear();
      for (const r of list) rows.set(String(r.id), r);
      ready = true;
      // the full answer supersedes the partial reads queued before it started, not the ones after
      for (const id of queuedBefore) pendingIds.delete(id);
      let newer = false;
      for (const [key, { seq, row }] of sinceFull) {
        if (seq <= fromSeq) continue;
        newer = true;
        if (row === null) rows.delete(key);
        else rows.set(key, row);
      }
      emit(newer ? snapshot() : list);
    } catch (e: any) {
      cfg.onError?.(e);
    } finally {
      if (--fullReads === 0) sinceFull.clear();
    }
  };
  const scheduleFull = () => {
    if (fullTimer) clearTimeout(fullTimer);
    fullTimer = setTimeout(fetchNow, 250);
  };
  const flushIds = async () => {
    const ids = [...pendingIds];
    pendingIds.clear();
    if (!ids.length) return;
    try {
      const fresh = await cfg.fetchByIds(ids);
      if (dead) return;
      const got = new Set<string>();
      for (const r of fresh) {
        const key = String(r.id);
        got.add(key);
        if (cfg.accept && !cfg.accept(r)) { rows.delete(key); noteLive(key, null); } else { rows.set(key, r); noteLive(key, r); }
      }
      for (const id of ids) {
        if (!got.has(id)) { rows.delete(id); noteLive(id, null); } // gone, or no longer visible to us
      }
      emit(snapshot());
    } catch (e: any) {
      cfg.onError?.(e);
    }
  };
  const queueId = (id: string) => {
    pendingIds.add(id);
    if (idsTimer) clearTimeout(idsTimer);
    idsTimer = setTimeout(flushIds, 150);
  };

  const onParentChange = (payload?: RealtimePayload) => {
    if (dead) return;
    cfg.live?.onPayload?.();
    if (mode === 'refetch' || !ready) { scheduleFull(); return; }
    const type = payload?.eventType;
    if (type === 'DELETE') {
      const id = payload?.old?.id;
      if (id == null) { scheduleFull(); return; }
      noteLive(String(id), null);
      if (rows.delete(String(id))) emit(snapshot());
      return;
    }
    if (type !== 'INSERT' && type !== 'UPDATE') { scheduleFull(); return; }
    const row = payload?.new as Row | undefined;
    const id = row?.id;
    if (id == null) { scheduleFull(); return; }
    const key = String(id);
    if (cfg.accept && !cfg.accept(row!)) {
      noteLive(key, null);
      if (rows.delete(key)) emit(snapshot());
      return;
    }
    const existing = rows.get(key);
    if (!existing || type === 'INSERT') {
      // A row we do not hold (or a brand-new one) has no embeds in the
      // payload. Showing it bare meant a freshly created order rendered with
      // ZERO products for ~200 ms (the card's "no products" notice flashed
      // on every new order); wait for the one read that brings its embeds.
      queueId(key);
      return;
    }
    const merged = { ...existing, ...row } as Row;
    rows.set(key, merged);
    noteLive(key, merged); // with its embeds, so a read that missed the row never paints it bare
    emit(snapshot());
  };
  const onChildChange = (fk: string) => (payload?: RealtimePayload) => {
    if (dead) return;
    cfg.live?.onPayload?.();
    if (mode === 'refetch' || !ready) { scheduleFull(); return; }
    const parentId = payload?.new?.[fk] ?? payload?.old?.[fk];
    if (parentId == null) { scheduleFull(); return; } // PK-only DELETE payload without the FK
    queueId(String(parentId));
  };

  fetchNow();
  cfg.live?.onResync?.(() => { if (!dead) scheduleFull(); });
  // One status tracker PER channel: they join at different moments, and a
  // shared tracker would read the second channel's first join as a re-join.
  // They share ONE pending resync, so a drop costs one full read, not one per channel.
  const pendingResync = sharedResync(() => { if (!dead) scheduleFull(); });
  const resync = () => {
    const track = channelResync(() => { if (!dead) scheduleFull(); }, pendingResync);
    return (status: string) => { cfg.live?.onStatus?.(status); track(status); };
  };
  const rand = () => Math.random().toString(36).slice(2, 8);
  const channels = [
    sb()
      .channel(`sub_${cfg.parent}_${rand()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: cfg.parent }, onParentChange)
      .subscribe(resync()),
    ...(cfg.children || []).map((child) => sb()
      .channel(`sub_${child.table}_${rand()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: child.table }, onChildChange(child.fk))
      .subscribe(resync())),
  ];
  return () => {
    dead = true;
    if (fullTimer) clearTimeout(fullTimer);
    if (idsTimer) clearTimeout(idsTimer);
    pendingResync.cancel();
    channels.forEach((ch) => sb().removeChannel(ch));
  };
}

// ── shared value converters (Postgres row ⇄ app types) ─────────────────────
// The app speaks epoch-ms numbers everywhere; Postgres speaks timestamptz.
export const msFromISO = (v: string | null | undefined): number | undefined =>
  v == null ? undefined : new Date(v).getTime();
export const msFromISO0 = (v: string | null | undefined): number =>
  v == null ? 0 : new Date(v).getTime();
export const isoFromMs = (v: number | null | undefined): string | null =>
  v == null ? null : new Date(v).toISOString();
export const numOr0 = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0);
